import moment from 'moment';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { getDashboardStats } from '~/javascripts/admin/actions/dashboard';
import { getPrivateMoneyAccountStatistics } from '~/javascripts/admin/actions/private-moneys';
import { useAppDispatch, useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { formatNum } from '~/javascripts/admin/utils';
import Grid from '../../atoms/Grid';
import BreadCrumb from '../../molecules/BreadCrumb';
import OverlayLoader from '../../molecules/OverlayLoader';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import DashboardCard from './DashboardCard';
import FilterForm from './FilterForm';

const PageWrapper = styled.div``;

const FormGrid = styled(Grid.Container)`
  margin-top: 10px;
`;

const StatisticsGrid = styled(Grid.Container)`
  margin-top: 10px;
  position: relative;
`;

const CRUMBS = [{ value: 'ホーム', href: '/', isCurrentPage: false }];

const Home = () => {
  const dispatch = useAppDispatch();

  const { id: privateMoneyId } = useAppSelector(selectUserPrivateMoney);
  const { user, dashboard } = useAppSelector((state) => ({
    user: state.user,
    dashboard: state.dashboard,
  }));

  const { topup, payment, balance } = dashboard;
  const canShowBalance = !!balance;

  const canShowAccountStatistics = user.isIssuerOf(privateMoneyId) || user.isServiceUser();

  const [accountStatistics, setAccountStatistics] =
    useState<Awaited<ReturnType<typeof getPrivateMoneyAccountStatistics>>>();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const topupTotalRows = [
    { label: 'マネー', value: `${formatNum(topup.money_amount)}` },
    { label: 'ポイント', value: `${formatNum(topup.raw_point_amount)}` },
    { label: 'ポイント（キャンペーン付与分）', value: `${formatNum(topup.campaign_point_amount)}` },
  ];

  const paymentTotalRows = [
    { label: 'マネー', value: `${formatNum(payment.money_amount)}` },
    { label: 'ポイント', value: `${formatNum(payment.raw_point_amount)}` },
    {
      label: 'ポイント（キャンペーン付与分）',
      value: `${formatNum(payment.campaign_point_amount)}`,
    },
  ];

  const moneyBalanceRows = canShowBalance
    ? [
        {
          label: `${moment(balance.saved_at).format('YY年MM月')}中に失効`,
          value: `${formatNum(balance.expire_this_month_amount.money_amount)}`,
        },
        {
          label: `${moment(balance.saved_at).add(1, 'month').format('YY年MM月')}中に失効`,
          value: `${formatNum(balance.expire_next_month_amount.money_amount)}`,
        },
        {
          label: `${moment(balance.saved_at).add(2, 'month').format('YY年MM月')}中に失効`,
          value: `${formatNum(balance.expire_after_next_month_amount.money_amount)}`,
        },
      ]
    : [];

  const pointBalanceRows = canShowBalance
    ? [
        {
          label: `${moment(balance.saved_at).format('YY年MM月')}中に失効`,
          value: `${formatNum(balance.expire_this_month_amount.point_amount)}`,
        },
        {
          label: `${moment(balance.saved_at).add(1, 'month').format('YY年MM月')}中に失効`,
          value: `${formatNum(balance.expire_next_month_amount.point_amount)}`,
        },
        {
          label: `${moment(balance.saved_at).add(2, 'month').format('YY年MM月')}中に失効`,
          value: `${formatNum(balance.expire_after_next_month_amount.point_amount)}`,
        },
      ]
    : [];

  const onQueryChange = async ({
    dashboardQuery,
    accountStatisticsQuery,
    privateMoneyId,
  }: {
    dashboardQuery: string;
    accountStatisticsQuery: string;
    privateMoneyId: string;
  }) => {
    try {
      setIsLoading(true);

      const [_, accountStatisticsData] = await Promise.all([
        dispatch(getDashboardStats(dashboardQuery)),
        canShowAccountStatistics
          ? getPrivateMoneyAccountStatistics(privateMoneyId, accountStatisticsQuery)
          : Promise.resolve(undefined),
      ]);

      if (canShowAccountStatistics) {
        setAccountStatistics(accountStatisticsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.body.style.backgroundColor = '#F9F9F9';

    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <SelectedPrivateMoneyDisplay />
      <FormGrid columnGap={8} rowGap={8}>
        {canShowAccountStatistics && (
          <Grid.Item size={4}>
            <DashboardCard
              headerLabel="累計会員数"
              headerValue={`${formatNum(accountStatistics?.total_accounts_until_today)}`}
            />
          </Grid.Item>
        )}
        <Grid.Item nextRow size={12}>
          <FilterForm onQueryChange={onQueryChange} />
        </Grid.Item>
      </FormGrid>
      <OverlayLoader isLoading={isLoading}>
        <StatisticsGrid columnGap={8} rowGap={8}>
          <Grid.Item nextRow size={4}>
            <DashboardCard headerLabel="チャージ件数" headerValue={formatNum(topup.count)} />
          </Grid.Item>
          <Grid.Item size={4}>
            <DashboardCard headerLabel="支払い件数" headerValue={formatNum(payment.count)} />
          </Grid.Item>
          {canShowAccountStatistics && (
            <Grid.Item size={4}>
              <DashboardCard
                headerLabel="会員数"
                headerValue={`${formatNum(accountStatistics?.total_amount_accounts)}`}
              />
            </Grid.Item>
          )}
          <Grid.Item nextRow size={4}>
            <DashboardCard
              headerLabel="チャージ総額"
              headerValue={`¥${formatNum(topup.money_amount + topup.raw_point_amount)}`}
              cardRows={topupTotalRows}
            />
          </Grid.Item>
          <Grid.Item size={4}>
            <DashboardCard
              headerLabel="支払い総額"
              headerValue={`¥${formatNum(payment.money_amount + payment.raw_point_amount)}`}
              cardRows={paymentTotalRows}
            />
          </Grid.Item>
          {canShowBalance && (
            <>
              <Grid.Item nextRow size={4}>
                <DashboardCard
                  headerLabel="マネー残高"
                  headerValue={`¥${formatNum(balance.total_amount.money_amount)}`}
                  cardRows={moneyBalanceRows}
                />
              </Grid.Item>
              <Grid.Item size={4}>
                <DashboardCard
                  headerLabel="ポイント残高"
                  headerValue={`¥${formatNum(balance.total_amount.point_amount)}`}
                  cardRows={pointBalanceRows}
                />
              </Grid.Item>
            </>
          )}
        </StatisticsGrid>
      </OverlayLoader>
    </PageWrapper>
  );
};

export default Home;
